/**
 * EMZI NEXUS V6.4 - Live Studio V2 API Client
 * 10-step wizard for live streaming script generation
 */
import { BACKEND_URL } from '../utils/backendUrl';

// ============================================================
// SESSION MANAGEMENT
// ============================================================

export const createLiveSession = async (token, sessionId, userId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/session/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      user_id: userId
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create live session');
  }
  
  return response.json();
};

export const getLiveSession = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/session/${sessionId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get live session');
  }
  
  return response.json();
};

// ============================================================
// STEP 1: PLATFORM & HOST PROFILE
// ============================================================

export const setPlatformAndHost = async (token, sessionId, platformData) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/platform-host`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      platform: platformData.platform,
      duration_minutes: platformData.durationMinutes,
      cta_interval: platformData.ctaInterval,
      session_type: platformData.sessionType,
      hosts: platformData.hosts
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save platform and host data');
  }
  
  return response.json();
};

// ============================================================
// STEP 2: OBJECTIVE
// ============================================================

export const setLiveObjective = async (token, sessionId, objectiveType) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/objective`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      objective_type: objectiveType
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set live objective');
  }
  
  return response.json();
};

// ============================================================
// STEP 3: TARGET AUDIENCE + PANGGILAN + NADA
// ============================================================

export const setLiveAudience = async (token, sessionId, audienceData) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/audience`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      ...audienceData
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set live audience');
  }
  
  return response.json();
};

// Get AI suggestion for panggilan and nada based on host and audience
export const getAISuggestion = async (token, hostProfile, audienceProfile) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/ai-suggest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      host_age_range: hostProfile.ageRange,
      host_gender: hostProfile.gender,
      host_personality: hostProfile.personality,
      audience_age_ranges: audienceProfile.ageRanges,
      audience_gender: audienceProfile.gender
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get AI suggestion');
  }
  
  return response.json();
};

// ============================================================
// STEP 4: KNOWLEDGE SELECTION
// ============================================================

export const selectLiveKnowledge = async (token, sessionId, selections) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/knowledge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      selected_products: selections.products || [],
      selected_services: selections.services || [],
      selected_values: selections.values || [],
      selected_ambassador: selections.ambassador || null
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to select knowledge');
  }
  
  return response.json();
};

// ============================================================
// STEP 5: SPIDER WEB (INFLUENCE WEIGHTS)
// ============================================================

export const setLiveSpiderWeights = async (token, sessionId, weights) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/spider-weights`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      weights: weights
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set spider weights');
  }
  
  return response.json();
};

// ============================================================
// STEP 6: EVIDENCE MODE
// ============================================================

export const setLiveEvidenceMode = async (token, sessionId, evidenceMode) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/evidence-mode`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      evidence_mode: evidenceMode
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set evidence mode');
  }
  
  return response.json();
};

// ============================================================
// STEP 7: REVIEW & PRIME SOA
// ============================================================

export const primeLiveSOA = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/soa/prime`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to prime Live SOA questions');
  }
  
  return response.json();
};

// ============================================================
// STEP 8: SOA DRILL (LIVE-SPECIFIC)
// ============================================================

export const evaluateLiveSOA = async (token, sessionId, questionIndex, answer, attemptNo) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/soa/evaluate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      question_index: questionIndex,
      answer: answer,
      attempt_no: attemptNo
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to evaluate SOA answer');
  }
  
  return response.json();
};

export const acceptLiveAIRescue = async (token, sessionId, questionIndex, aiRescueAnswer = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/soa/accept-rescue`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      question_index: questionIndex,
      ai_rescue_answer: aiRescueAnswer
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to accept AI Rescue');
  }
  
  return response.json();
};

export const finalizeLiveSOA = async (token, sessionId, proceedAnyway = false) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/soa/finalize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      proceed_anyway: proceedAnyway
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to finalize SOA Drill');
  }
  
  return response.json();
};

// ============================================================
// STEP 9: SCRIPT GENERATION
// ============================================================

export const generateLiveScript = async (token, sessionId, options) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/script/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      output_format: options.outputFormat, // talking_points, full_script, hybrid
      language_style: options.languageStyle // santai, pasar, formal
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate live script');
  }
  
  return response.json();
};

// ============================================================
// STEP 10: REVIEW & SUBMIT
// ============================================================

export const submitLiveForApproval = async (token, sessionId, projectName = null, notes = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      project_name: projectName,
      notes: notes
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to submit for approval');
  }
  
  return response.json();
};

// ============================================================
// TELEPROMPTER (POST-APPROVAL)
// ============================================================

export const getTeleprompterData = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/teleprompter/${sessionId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get teleprompter data');
  }
  
  return response.json();
};

export const updateTeleprompterProgress = async (token, sessionId, sectionId, status) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/teleprompter/progress`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      section_id: sectionId,
      status: status // completed, skipped
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update progress');
  }
  
  return response.json();
};

// ============================================================
// SUPERVISOR ACTIONS
// ============================================================

export const getLiveSubmissionsQueue = async (token, teamId = 'Live') => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/submissions/queue?team_id=${teamId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get live submissions queue');
  }
  
  return response.json();
};

export const approveLiveSubmission = async (token, sessionId, decision, comment = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/live/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      decision: decision, // approve, reject, request_changes
      comment: comment
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to process approval');
  }
  
  return response.json();
};
